import { useCallback, useMemo, useState } from 'react';
import { Group, Panel, Separator } from 'react-resizable-panels';
import { useRenderEmail } from '@/api/email-builder/email-builder.hooks';
import type {
  ContactPersonMeta,
  EmailBlock,
  RenderEmailRequest,
} from '@/api/email-builder/email-builder.types.ts';
import { useSalesPersonCandidates } from '@/api/user/user.hooks';
import type { SalesPersonCandidate } from '@/api/user/user.types';
import { usePermissions } from '@/hooks/use-permissions';
import { createBlock, newBlockId, useEmailBlocks } from './hooks/use-email-blocks';
import { useDebouncedValue } from './hooks/use-debounced-value';
import { EditorPane } from './components/editor-pane.component';
import { PreviewPane } from './components/preview-pane.component';
import { AiChatPane, AgentMark } from './components/ai-chat-pane.component';
import { RecipientsPane } from './components/recipients-pane.component';
import './email-builder.css';

/**
 * Builds the starter draft. Pre-fills the greeting and sign-off as
 * variable chips so a brand-new draft is already personalised — the
 * author types into the empty paragraph between them and the render
 * endpoint substitutes `{{anrede}}` / `{{grussformel}}` against the
 * picked preview recipient (and per recipient at send time, later).
 *
 * The chip HTML uses the same `<span data-variable="…">{{…}}</span>`
 * form TipTap emits, so it parses straight back into Variable nodes
 * on mount. The backend resolver strips the wrappers before
 * substitution, so the rendered email is clean.
 *
 * Two text blocks (greeting / sign-off) rather than one so the author
 * can drop content blocks — products, banners, images — between them
 * without having to break a single block apart.
 */
/** Placeholder body copy in the middle text block. Lorem ipsum so it's
 * obviously a placeholder — the author selects-all + types over it. */
const LOREM_IPSUM_BODY =
  '<p>Lorem ipsum dolor sit amet, consectetur adipiscing elit. ' +
  'Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. ' +
  'Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris ' +
  'nisi ut aliquip ex ea commodo consequat.</p>' +
  '<p>Duis aute irure dolor in reprehenderit in voluptate velit esse ' +
  'cillum dolore eu fugiat nulla pariatur.</p>';

function buildInitialBlocks(): EmailBlock[] {
  return [
    {
      id: newBlockId(),
      type: 'text',
      data: {
        html:
          '<p><span data-variable="anrede">{{anrede}}</span>,</p><p></p>',
      },
    },
    {
      id: newBlockId(),
      type: 'text',
      data: { html: LOREM_IPSUM_BODY },
    },
    {
      id: newBlockId(),
      type: 'text',
      data: {
        html:
          '<p><span data-variable="grussformel">{{grussformel}}</span></p>',
      },
    },
  ];
}
// `createBlock` stays referenced — `useEmailBlocks.addBlock` and the
// `AddBlockMenu` flow continue to use it for every NEW block the user
// inserts; only the initial draft uses the pre-filled skeleton above.
void createBlock;

/** Empty Ansprechpartner — enabled by default; filled when candidates load. */
const EMPTY_CONTACT_PERSON: ContactPersonMeta = {
  enabled: true,
  name: '',
  email: '',
  phone: '',
  officePhone: '',
};

/** Maps a sales-person candidate to the Ansprechpartner payload shape. */
function candidateToContactPerson(
  candidate: SalesPersonCandidate,
): Omit<ContactPersonMeta, 'enabled'> {
  return {
    name: `${candidate.firstName} ${candidate.lastName}`.trim(),
    email: candidate.email ?? '',
    phone: candidate.mobilePhone ?? '',
    officePhone: candidate.officePhone ?? '',
  };
}

/** Which view fills the left pane. */
type LeftMode = 'editor' | 'ai' | 'recipients';

/**
 * Visual email builder — the left pane toggles between the block editor and an
 * AI assistant chat, the right pane shows the backend-rendered live preview.
 * Both left views stay mounted (hidden, not unmounted), so editor state and the
 * chat conversation survive switching back and forth. Drafts are session-only.
 */
export function EmailBuilderPage() {
  // Lazy initialisation: `buildInitialBlocks` allocates fresh block ids
  // (via `newBlockId`), so the call has to happen *once* per mount.
  // Calling the builder inline on every render would allocate twin
  // throw-away blocks each time, so memo via `useState`'s lazy initializer.
  const [initialBlocks] = useState(buildInitialBlocks);
  const { blocks, addBlock, updateBlock, removeBlock, moveBlock, replaceBlocks } =
    useEmailBlocks(initialBlocks);
  const [subject, setSubject] = useState('');
  const [preheader, setPreheader] = useState('');
  const [mode, setMode] = useState<LeftMode>('editor');
  /** Preview-only — drives `{{anrede}}` etc. in the preview, never sent. */
  const [previewRecipientId, setPreviewRecipientId] = useState<number | null>(
    null,
  );

  // Recipient state is owned by the page (not the recipients pane) so that
  // switching to the editor / AI tab and back keeps the user's recipient
  // picks intact — same survival guarantee the AI chat already has.
  const [recipientContactListIds, setRecipientContactListIds] = useState<
    number[]
  >([]);
  const [recipientAdHocContactIds, setRecipientAdHocContactIds] = useState<
    number[]
  >([]);
  const totalRecipientSlots =
    recipientContactListIds.length + recipientAdHocContactIds.length;

  // Ansprechpartner state is split so the default pre-fills from the caller's
  // own sales-person record at render time (no setState-in-effect dance):
  //   * `contactPersonEnabled` — toggle, persists across picks
  //   * `pickedSalesPersonCode` — `undefined` until the user touches the
  //     picker; the AI override or a non-self default still flow through
  //     `contactPersonOverride`
  //   * `contactPersonOverride` — full override (used by the AI assistant)
  const { salesPersonCode: ownSalesPersonCode } = usePermissions();
  const { data: salesPersonCandidates } = useSalesPersonCandidates();
  const [contactPersonEnabled, setContactPersonEnabled] = useState(true);
  const [pickedSalesPersonCode, setPickedSalesPersonCode] = useState<
    number | null | undefined
  >(undefined);
  const [contactPersonOverride, setContactPersonOverride] = useState<
    Omit<ContactPersonMeta, 'enabled'> | null
  >(null);

  /** Resolves the active sales-person code: explicit pick > AI override > caller. */
  const selectedSalesPersonCode = useMemo<number | null>(() => {
    if (pickedSalesPersonCode !== undefined) return pickedSalesPersonCode;
    if (contactPersonOverride?.email && salesPersonCandidates) {
      return (
        salesPersonCandidates.find(
          (c) => c.email === contactPersonOverride.email,
        )?.salesPersonCode ?? null
      );
    }
    return ownSalesPersonCode ?? null;
  }, [
    pickedSalesPersonCode,
    contactPersonOverride,
    salesPersonCandidates,
    ownSalesPersonCode,
  ]);

  const contactPerson = useMemo<ContactPersonMeta>(() => {
    // Explicit "no contact" pick wins outright.
    if (pickedSalesPersonCode === null && !contactPersonOverride) {
      return { ...EMPTY_CONTACT_PERSON, enabled: contactPersonEnabled };
    }
    const candidate =
      selectedSalesPersonCode != null
        ? salesPersonCandidates?.find(
            (c) => c.salesPersonCode === selectedSalesPersonCode,
          )
        : undefined;
    const base = candidate
      ? candidateToContactPerson(candidate)
      : (contactPersonOverride ?? {
          name: '',
          email: '',
          phone: '',
          officePhone: '',
        });
    return { ...base, enabled: contactPersonEnabled };
  }, [
    pickedSalesPersonCode,
    contactPersonOverride,
    selectedSalesPersonCode,
    salesPersonCandidates,
    contactPersonEnabled,
  ]);

  const request = useMemo<RenderEmailRequest>(
    () => ({
      subject,
      preheader,
      contactPerson,
      blocks,
      previewRecipient:
        previewRecipientId != null
          ? { contactEmployeeId: previewRecipientId }
          : undefined,
    }),
    [subject, preheader, contactPerson, blocks, previewRecipientId],
  );

  /** Records the user's pick — `null` clears the Ansprechpartner. */
  const handleSelectSalesPerson = useCallback((code: number | null) => {
    setPickedSalesPersonCode(code);
    setContactPersonOverride(null);
  }, []);

  /** Applies a draft the AI assistant produced to the live editor state. */
  const applyAiEdits = useCallback(
    (next: RenderEmailRequest) => {
      setSubject(next.subject ?? '');
      setPreheader(next.preheader ?? '');
      if (next.contactPerson) {
        setContactPersonEnabled(next.contactPerson.enabled);
        setContactPersonOverride({
          name: next.contactPerson.name,
          email: next.contactPerson.email,
          phone: next.contactPerson.phone,
          officePhone: next.contactPerson.officePhone,
        });
        // Drop any prior manual pick — the AI override now drives the picker
        // selection via email matching in `selectedSalesPersonCode`.
        setPickedSalesPersonCode(undefined);
      }
      replaceBlocks(next.blocks);
    },
    [replaceBlocks],
  );
  const debouncedRequest = useDebouncedValue(request, 400);
  const { data, isFetching, isError } = useRenderEmail(debouncedRequest);

  return (
    <Group
      orientation="horizontal"
      id="email-builder"
      className="eb-main"
      defaultLayout={{ editor: 46, preview: 54 }}
    >
      <Panel id="editor" minSize={360} className="eb-pane">
        <div className="eb-modebar" role="tablist" aria-label="Ansicht wechseln">
          <button
            type="button"
            role="tab"
            id="eb-tab-editor"
            aria-selected={mode === 'editor'}
            aria-controls="eb-panel-editor"
            className={`eb-mode${mode === 'editor' ? ' is-active' : ''}`}
            onClick={() => setMode('editor')}
          >
            <svg viewBox="0 0 16 16" aria-hidden="true">
              <rect
                x="2"
                y="2.5"
                width="12"
                height="3.4"
                rx="1"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.4"
              />
              <rect
                x="2"
                y="7.6"
                width="12"
                height="5.9"
                rx="1"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.4"
              />
            </svg>
            Editor
          </button>
          <button
            type="button"
            role="tab"
            id="eb-tab-ai"
            aria-selected={mode === 'ai'}
            aria-controls="eb-panel-ai"
            className={`eb-mode${mode === 'ai' ? ' is-active' : ''}`}
            onClick={() => setMode('ai')}
          >
            <AgentMark />
            KI-Assistent
            <span className="eb-mode-dot" aria-hidden="true" />
          </button>
          <button
            type="button"
            role="tab"
            id="eb-tab-recipients"
            aria-selected={mode === 'recipients'}
            aria-controls="eb-panel-recipients"
            className={`eb-mode${mode === 'recipients' ? ' is-active' : ''}`}
            onClick={() => setMode('recipients')}
          >
            <svg viewBox="0 0 16 16" aria-hidden="true">
              <path
                d="M2.6 12.4c.6-1.8 2.4-3 4.4-3s3.8 1.2 4.4 3M7 8a2.4 2.4 0 100-4.8A2.4 2.4 0 007 8zm6 4c.4-1 1.4-1.8 2.5-1.8M13 7.5a1.7 1.7 0 100-3.4 1.7 1.7 0 000 3.4z"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.3"
                strokeLinecap="round"
              />
            </svg>
            Empfänger
            {totalRecipientSlots > 0 && (
              <span
                className="eb-mode-count"
                aria-label={`${totalRecipientSlots} ausgewählt`}
              >
                {totalRecipientSlots}
              </span>
            )}
          </button>
        </div>

        <div className="eb-views">
          <div
            className="eb-view"
            id="eb-panel-editor"
            role="tabpanel"
            aria-labelledby="eb-tab-editor"
            hidden={mode !== 'editor'}
          >
            <EditorPane
              blocks={blocks}
              subject={subject}
              preheader={preheader}
              contactPerson={contactPerson}
              selectedSalesPersonCode={selectedSalesPersonCode}
              onSubjectChange={setSubject}
              onPreheaderChange={setPreheader}
              onContactPersonEnabledChange={setContactPersonEnabled}
              onSelectSalesPerson={handleSelectSalesPerson}
              onAddBlock={addBlock}
              onUpdateBlock={updateBlock}
              onRemoveBlock={removeBlock}
              onMoveBlock={moveBlock}
            />
          </div>
          <div
            className="eb-view"
            id="eb-panel-ai"
            role="tabpanel"
            aria-labelledby="eb-tab-ai"
            hidden={mode !== 'ai'}
          >
            <AiChatPane draft={request} onApplyEdits={applyAiEdits} />
          </div>
          <div
            className="eb-view"
            id="eb-panel-recipients"
            role="tabpanel"
            aria-labelledby="eb-tab-recipients"
            hidden={mode !== 'recipients'}
          >
            <RecipientsPane
              draft={request}
              subject={subject}
              onSubjectChange={setSubject}
              contactListIds={recipientContactListIds}
              onContactListIdsChange={setRecipientContactListIds}
              adHocContactIds={recipientAdHocContactIds}
              onAdHocContactIdsChange={setRecipientAdHocContactIds}
            />
          </div>
        </div>
      </Panel>
      <Separator className="eb-resize-handle" />
      <Panel id="preview" minSize={320} className="eb-pane">
        <PreviewPane
          html={data?.html}
          isLoading={isFetching}
          isError={isError}
          previewRecipientId={previewRecipientId}
          onPreviewRecipientChange={setPreviewRecipientId}
        />
      </Panel>
    </Group>
  );
}
